/**
 * fundUtils.js — Derived fund metrics
 *
 * Computes values from MOCK_FUNDS + MOCK_SPVS rather than storing them.
 * Single computation point for all fund-level components.
 */

const fmtM = (n) => (n != null ? `£${(n / 1_000_000).toFixed(0)}m` : '—');

/**
 * Returns derived metrics for a single fund.
 * @param {object} fund  - A MOCK_FUNDS entry
 * @param {array}  allSPVs - The full MOCK_SPVS array
 */
export function getFundMetrics(fund, allSPVs) {
  const lps = fund?.lpCommitments ?? [];
  const totalCommitted   = lps.reduce((s, lp) => s + (lp.commitmentAmount ?? 0), 0);
  const totalCalled      = lps.reduce((s, lp) => s + (lp.calledAmount ?? 0), 0);
  const totalDistributed = lps.reduce((s, lp) => s + (lp.distributedAmount ?? 0), 0);
  const calledPct        = totalCommitted ? ((totalCalled / totalCommitted) * 100).toFixed(1) : '0.0';

  const fundSPVs    = (allSPVs ?? []).filter((s) => fund?.spvIds?.includes(s.id));
  const totalDeployed = fundSPVs.reduce((s, spv) => s + (spv.raisedAmount ?? 0), 0);
  const undeployed    = totalCalled - totalDeployed;

  return {
    totalCommitted,
    totalCalled,
    totalDistributed,
    calledPct,
    fundSPVs,
    totalDeployed,
    undeployed,
    lpCount: lps.length,
    spvCount: fund?.spvIds?.length ?? 0,
    // Formatted helpers
    fmtCommitted:   fmtM(totalCommitted),
    fmtCalled:      fmtM(totalCalled),
    fmtDistributed: fmtM(totalDistributed),
    fmtDeployed:    fmtM(totalDeployed),
    fmtUndeployed:  undeployed > 0 ? fmtM(undeployed) : '—',
  };
}

/**
 * Returns the GP allocation commitment row from an SPV's commitments array.
 */
export function getGPAllocation(spv) {
  return spv?.commitments?.find((c) => c.commitmentType === 'gp_allocation') ?? null;
}

/**
 * Returns only co-investor commitment rows from an SPV's commitments array.
 */
export function getCoInvestorCommitments(spv) {
  return (spv?.commitments ?? []).filter((c) => c.commitmentType === 'co_investor');
}
